import * as React from 'react';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { JumpTrigger, PauseTrigger, OverlayTrigger } from './trigger';
import playback from '../../state/playback/playback.actions';
import useTimer from '../../hooks/useTimer';
import Logger from '../../utils/Logger';

type TimeTrigger = {
  name: string,
  show: number,
  hide: ?string,
  type: number,
  pauseOnClick: ?boolean,
  overlayId: ?number,
  autoClose: ?boolean,
  autoCloseTime: ?number,
}

type Props = {
  timeTriggers: Array<TimeTrigger>,
  currentFrame: number,
  prevFrame: number | boolean, // FIXME: See blow fixme
  reduxPlay: Function,
  closeOverlayAndAnimateOut: Function,
  activeOverlay: Object,
}

const TimeTriggers = ({
  timeTriggers = [],
  currentFrame = 0,
  prevFrame,
  reduxPlay, // NOTE: this is added to use Redux to control playback
  closeOverlayAndAnimateOut,
  activeOverlay = {},
}: Props) => {
  const [triggerOverlayOpen, setTriggerOverlayOpen] = useState(false);
  const [setTimer, clearTimer] = useTimer(() => {
    Logger.info('Trigger :: Time is up, close overlay and resume video.');
    closeOverlayAndAnimateOut();
    reduxPlay();
  });

  useEffect(() => () => {
    clearTimer();
  }, [clearTimer]);

  const onDemandTrigger: TimeTrigger | Object = timeTriggers.find(
    trigger => isOnDemand(trigger.show, currentFrame, prevFrame),
  ) || {};

  const {
    type, hide, pauseOnClick, autoClose, autoCloseTime, overlayId,
  } = onDemandTrigger;

  useEffect(() => {
    if (autoClose && autoCloseTime && type === Trigger.pausePoint.id) {
      setTimer(autoCloseTime);
    }
  }, [type]);

  // NOTE: update state to control timer, maybe make a timer component make more sense
  if (!triggerOverlayOpen && isTriggerOverlayOpen(activeOverlay, overlayId)) {
    setTriggerOverlayOpen(true);
    if (autoClose) setTimer(autoCloseTime);
  }

  if (triggerOverlayOpen && isTriggerOverlayClosed(activeOverlay, overlayId)) {
    setTriggerOverlayOpen(false);
    clearTimer();
  }

  const handleEntered = () => {
    if (type === Trigger.jumpPoint.id) closeOverlayAndAnimateOut();
  }

  // NOTE: Overlay Point only open overlay. If it needs to pause video, add a pause point
  return (
    <>
      {type === Trigger.jumpPoint.id && <JumpTrigger to={hide} onEntered={handleEntered} />}
      {type === Trigger.pausePoint.id && <PauseTrigger />}
      {type === Trigger.customPoint.id && null}
      {type === Trigger.overlayPoint.id && <OverlayTrigger overlayId={overlayId} />}
      {type === Trigger.overlayPoint.id && pauseOnClick && <PauseTrigger />}
    </>
  );
};

/**
 * - Check if there is a time trigger on demand
 * - FIXME: it has a legacy gotcha here:
 * - prevFrame can be "false" or a frame number, type can change
 * - prevFrame "false" is for after seeked, should only check exactMatch(match forward)
 */

const isOnDemand = (inPoint: number, currentFrame: number, prevFrame: number | boolean): boolean => {
  const isExactMatch = Math.round(inPoint) === currentFrame;
  const isApprMatch = typeof prevFrame === 'number'
    && currentFrame - prevFrame >= 1
    && inPoint >= prevFrame
    && inPoint <= currentFrame;

  const startingRange = 5; // Video starting might jump a few frames
  const isPosterTriggerMatch = inPoint <= 1 && currentFrame > 0 && currentFrame < startingRange;

  return isExactMatch || isApprMatch || isPosterTriggerMatch;
}

const isTriggerOverlayOpen = (activeOverlay: Object, triggerOverlayId: ?number): boolean => {
  return !!triggerOverlayId && activeOverlay.overlayId === triggerOverlayId;
}

const isTriggerOverlayClosed = (activeOverlay: Object, triggerOverlayId: ?number): boolean => {
  // Closed by opening another overlay
  if (!!triggerOverlayId && activeOverlay.overlayId !== triggerOverlayId) return true;

  // No overlay is open
  return Object.keys(activeOverlay).length === 0;
}

class Trigger {
  static jumpPoint: Trigger = new Trigger('jumpPoint', 0);

  static pausePoint: Trigger = new Trigger('pausePoint', 1);

  static customPoint: Trigger = new Trigger('customPoint', 2);

  static overlayPoint: Trigger = new Trigger('overlayPoint', 3);

  name: string

  id: number

  constructor(name: string, id: number) {
    this.name = name;
    this.id = id;
  }

  toString(): string {
    return `Trigger.${this.name}`;
  }
}

const mapStateToProps = (state: Object) => ({
  timeTriggers: state.options.activeVideo.timeTriggers,
  currentFrame: state.video.currentFrame,
  prevFrame: state.video.prevFrame,
  activeOverlay: state.options.activeOverlay,
});

const mapDispatchToProps = (dispatch: any) => ({
  reduxPlay: () => dispatch(playback.reduxPlay()),
});

export default connect(mapStateToProps, mapDispatchToProps)(TimeTriggers);
